import { clsx } from "clsx";

type BadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "accent";

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  className?: string;
  size?: "sm" | "md";
}

export default function Badge({
  children,
  variant = "neutral",
  className,
  size = "md",
}: BadgeProps) {
  const variants = {
    success: "bg-green-500/20 text-green-400 border-green-500/30",
    warning: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    error: "bg-red-500/20 text-red-400 border-red-500/30",
    info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    neutral: "bg-white/10 text-text-secondary border-white/20",
    accent: "bg-accent-primary/20 text-accent-primary border-accent-primary/30",
  };

  const sizes = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-3 py-1 text-xs",
  };

  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full font-semibold border",
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {children}
    </span>
  );
}

// Badges específicos para status de orçamento
const statusConfig: Record<string, { variant: BadgeVariant; label: string }> = {
  draft: { variant: "neutral", label: "Rascunho" },
  sent: { variant: "info", label: "Enviado" },
  viewed: { variant: "warning", label: "Visualizado" },
  approved: { variant: "success", label: "Aprovado" },
  rejected: { variant: "error", label: "Rejeitado" },
  changes_requested: { variant: "info", label: "Alterações solicitadas" },
  expired: { variant: "neutral", label: "Expirado" },
  converted: { variant: "accent", label: "Convertido" },
};

interface StatusBadgeProps {
  status: string;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = statusConfig[status] || {
    variant: "neutral" as const,
    label: status,
  };

  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  );
}
